import { useEffect, useRef } from "react";
import { buildMask, clear, inkDebug, newInkState, render, spawn, step, type InkPulse, type InkState, type Rect } from "./ink-sim.js";

export { inkDebug, type InkPulse } from "./ink-sim.js";

/** Opaque controls the pigment slides UNDER (mask punch-out with edge bleed)
 *  so their text stays legible. Composite widgets (steppers, segmented
 *  controls, the duration pill) are masked as ONE container rect. Re-measured
 *  on spawn, resize, AND periodically from the rAF loop so sliding panels and
 *  mid-bloom toggles don't leave holes at stale positions. */
const OBSTACLE_SEL = 'button, [role="button"], label.check, .stepper, .mini-stepper, .unit-seg, .dur-toggle';

function collectObstacles(s: InkState, canvas: HTMLCanvasElement | null): void {
  const root = canvas?.closest<HTMLElement>(".task-sheet-root") ?? canvas?.closest<HTMLElement>(".card");
  if (!root || !canvas) return;
  const cr = canvas.getBoundingClientRect();
  const out: Rect[] = [];
  // null = ancestor doesn't clip; cached per collection run.
  const clipCache = new Map<Element, DOMRect | null>();
  const clipRectOf = (el: Element): DOMRect | null => {
    const hit = clipCache.get(el);
    if (hit !== undefined) return hit;
    const o = getComputedStyle(el);
    const clips = o.overflow !== "visible" || o.overflowY !== "visible" || o.overflowX !== "visible";
    const r = clips ? el.getBoundingClientRect() : null;
    clipCache.set(el, r);
    return r;
  };
  root.querySelectorAll<HTMLElement>(OBSTACLE_SEL).forEach((el) => {
    if (el.offsetParent === null) return; // display:none subtree
    let { left, top, right, bottom } = el.getBoundingClientRect();
    // Clip by every overflow-clipping ancestor: controls inside a collapsed or
    // mid-slide panel (grid-rows 0fr) still have layout rects — only their
    // actually-VISIBLE part may punch a hole (else: phantom clear splotches).
    for (let p = el.parentElement; p; p = p.parentElement) {
      const cl = clipRectOf(p);
      if (cl) {
        left = Math.max(left, cl.left);
        top = Math.max(top, cl.top);
        right = Math.min(right, cl.right);
        bottom = Math.min(bottom, cl.bottom);
      }
      if (p === root) break;
    }
    const w = right - left, h = bottom - top;
    if (w < 8 || h < 8) return;
    const x = left - cr.left, y = top - cr.top;
    if (x > s.W || y > s.H || x + w < 0 || y + h < 0) return;
    out.push({ x, y, w, h });
  });
  s.obstacles = out;
  buildMask(s);
}

export function InkCanvas({ pulse }: { pulse: InkPulse | null }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const S = useRef<InkState>(newInkState());

  useEffect(() => {
    const canvas = ref.current!;
    const s = S.current;
    s.lo = document.createElement("canvas");
    s.lctx = s.lo.getContext("2d");
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      s.dpr = Math.min(2, window.devicePixelRatio || 1);
      s.W = Math.max(1, r.width);
      s.H = Math.max(1, r.height);
      canvas.width = Math.round(s.W * s.dpr);
      canvas.height = Math.round(s.H * s.dpr);
      const k = 0.3; // low-res buffer → cheap, fluid blur on upscale
      s.lo!.width = Math.max(1, Math.round(s.W * k));
      s.lo!.height = Math.max(1, Math.round(s.H * k));
      collectObstacles(s, canvas);
      // Setting canvas dims WIPES the bitmap, and ResizeObserver fires AFTER
      // this frame's rAF already painted — without an immediate repaint every
      // frame of a card-growth animation presented an empty canvas (particles
      // "vanished" during panel slides and popped back after).
      if (s.parts.length > 0) render(s, canvas, s.last);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => { ro.disconnect(); cancelAnimationFrame(s.raf); };
  }, []);

  useEffect(() => {
    if (!pulse) return;
    const s = S.current;
    collectObstacles(s, ref.current);
    spawn(s, pulse);
    if (inkDebug()) {
      console.info(`[ink] spawn #${pulse.id} src=${pulse.src} col=${pulse.col} parts=${s.parts.length} obstacles=${s.obstacles.length} ${s.W}x${s.H}`);
    }
    start(s, ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulse?.id]);

  return <canvas ref={ref} className="ink-canvas" aria-hidden="true" />;
}

/** Idempotent: always (re)establishes a single clean loop (a frame that throws
 *  must not wedge the animation stopped). */
function start(s: InkState, canvas: HTMLCanvasElement | null): void {
  cancelAnimationFrame(s.raf);
  s.last = performance.now();
  let frame = 0;
  const loop = (now: number) => {
    // Clamp dt ≥ 0: the first rAF timestamp is vsync-aligned and can PRECEDE
    // the performance.now() taken at spawn — negative dt aged fresh particles
    // backwards (negative fade-in alpha = invisible frames).
    const dt = Math.max(0, Math.min(0.045, (now - s.last) / 1000));
    s.last = now;
    // Track sliding panels / toggles while a bloom is live (~5×/s; buildMask
    // skips when nothing moved).
    if (++frame % 12 === 0) collectObstacles(s, canvas);
    try { step(s, dt, now / 1000); render(s, canvas, now); } catch { /* keep looping */ }
    if (s.parts.length > 0) s.raf = requestAnimationFrame(loop);
    else {
      s.raf = 0;
      clear(s, canvas);
      if (inkDebug()) console.info("[ink] loop exit (all particles settled)");
    }
  };
  s.raf = requestAnimationFrame(loop);
}
